import React from 'react';
import Component from 'ShopUi/models/component';
import { createRoot } from 'react-dom/client';
import { EDialogEvents } from 'PyzShopUi/scripts/modules/events/dialogEvent';
import MiddlewareEventBus from 'PyzShopUi/scripts/react/common/lib/MiddlewareEventBus';
import { ProductLocalStoreAvailabilityEvent } from 'PyzShopUi/scripts/react/common/events/productLocalStoreAvailabilityEvent';
import IReactEvent from 'PyzShopUi/scripts/interfaces/article-list/IReactEvent';
import ProductLocalStoreAvailabilityInfo from './product-local-store-availability-info';
import ProductLocalStoreDetailInfo from './product-local-store-detail-info';
import EventBus from 'PyzShopUi/scripts/utils/eventBus';
import { LocalStoreArticleAvailabilityResponse } from '../../organisms/product-local-store-availability/interface';

export default class ProductLocalStoreAvailabilityDialog extends Component {

    private eventBus: EventBus;
    private dialogElement: HTMLElement;
    private localStoreSelect: HTMLSelectElement;
    private rootCreated = false;
    private infoPlaceholder;

    protected init() {
        const localStoreAvailabilityLink: HTMLElement = document.querySelector('.product-local-store-availability-short-info');
        if (!localStoreAvailabilityLink) {
            return;
        }

        this.eventBus = EventBus.getInstance();
        this.dialogElement = document.querySelector('#local-store-availability-dialog');
        this.localStoreSelect = this.dialogElement.querySelector('select');
        this.addCustomEventListener();
    }

    protected readyCallback(): void {
    }

    private readonly addCustomEventListener = (): void => {
        this.eventBus.subscribe(EDialogEvents.OPEN_DIALOG, (target: {id: string}): void => {
            const dialogElement: HTMLElement = this.dialogElement;
            if (target !== undefined && target.id === dialogElement.id) {
                if (!this.validateRequestParams()) {
                    return;
                }

                this.loadLocalStoreArticleAvailability();
            }
        });

        MiddlewareEventBus.getInstance().subscribe((event: IReactEvent<{}>) => {
            if (event.type !== ProductLocalStoreAvailabilityEvent.FETCH_DATA) {
                return;
            }

            if (!this.validateRequestParams()) {
                return;
            }

            this.loadLocalStoreArticleAvailability(event.payload);
        });

        this.addLocalStoreSelectListener();
    }

    private readonly addLocalStoreSelectListener = (): void => {
        this.localStoreSelect.addEventListener('change', (event: Event): void => {
            event.preventDefault();

            if (!this.validateRequestParams()) {
                return;
            }

            this.loadLocalStoreArticleAvailability();
        });
    }

    private readonly validateRequestParams = (): boolean => {
        const localStoreId: string = this.localStoreSelect.value;
        if (!localStoreId || localStoreId === '') {
            return false;
        }

        const apiUrlElement: HTMLElement = this.dialogElement.querySelector('[data-api-url]');
        const localStoreArticleAvailabilityUrl: string = apiUrlElement.dataset.apiUrl;
        if (!localStoreArticleAvailabilityUrl) {
            return false;
        }

        const concreteIdElement: HTMLElement = document.querySelector('.product-local-store-availability-short-info [data-concrete-id]');
        const concreteId: string = concreteIdElement?.dataset.concreteId;
        if (!concreteId) {
            return false;
        }

        return true;
    }

    private readonly loadLocalStoreArticleAvailability = (payload): void => {
        const localStoreId: string = this.localStoreSelect.value;
        const apiUrlElement: HTMLElement = this.dialogElement.querySelector('[data-api-url]');
        const localStoreArticleAvailabilityUrl: string = apiUrlElement.dataset.apiUrl;
        const concreteIdElement: HTMLElement = document.querySelector('.product-local-store-availability-short-info [data-concrete-id]');
        const concreteId: string = payload?.concreteId ?? concreteIdElement.dataset.concreteId;
        const url: string = this.getApiRequestUrl(localStoreArticleAvailabilityUrl, concreteId, localStoreId);

        fetch(url)
            .then((res: Response) => res.json())
            .then((data: LocalStoreArticleAvailabilityResponse): void => {
                const available = data.storeAvailability?.quantity ? data.storeAvailability?.quantity > 0 : false;
                const infoPlaceholder: HTMLElement = this.dialogElement.querySelector('[data-information-placeholder]');

                if (data.storeDetails) {

                    if (!this.rootCreated) {
                        const infoRoot = createRoot(infoPlaceholder);
                        this.infoPlaceholder = infoRoot;
                    }

                    this.rootCreated = true;

                    this.infoPlaceholder.render(
                        <>
                            <ProductLocalStoreAvailabilityInfo isAvailable={available} isAvailableForClickAndCollect={data.storeDetails.is_available_for_click_and_collect} phoneNumber={data.storeDetails.phone_number} />
                            <ProductLocalStoreDetailInfo store={data.storeDetails} />
                        </>
                    );

                }

                MiddlewareEventBus.getInstance().publish({type: ProductLocalStoreAvailabilityEvent.UPDATE_DATA, payload: {
                        concreteId,
                        selectedStoreId: localStoreId,
                        data
                    }
                });
            });
    }

    private readonly getApiRequestUrl = (apiUrl: string, concreteId: string, storeId: string): string => {
        const apiUrlWithParams = new URL(apiUrl, window.location.origin);
        apiUrlWithParams.searchParams.append('concrete-id', concreteId);
        apiUrlWithParams.searchParams.append('store-id', storeId);

        return apiUrlWithParams.toString();
    }
}
